import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const timeToMinutes = (s) => {
    if (!s || s === '--:--') return -1;
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const StateScreen = ({ msg, isError }) => (
    <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#fdfbf7', direction: 'rtl',
    }}>
        <span style={{ fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8' }}>{msg}</span>
    </div>
);

const Ribbon = ({
    moadData, processedCustomTimes, loading, error,
    title = 'מועד', showTitle = true, days = [],
    entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [],
    drashotByDay = {},
    showLeyning, showHaftarah, leyningDisplayMode,
}) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['Star'];

    if (loading) return <StateScreen msg="טוען זמני מועד..." />;
    if (error)   return <StateScreen msg={error} isError />;
    if (!moadData?.days?.length) return <StateScreen msg="אין נתוני מועד" />;

    const firstDay = moadData.days[0];
    const lastDay  = moadData.days[moadData.days.length - 1];
    const entryTime = firstDay?.entryTime || '--:--';
    const exitTime  = lastDay?.exitTime   || '--:--';
    const entryLabel = dayLabels[0]?.entry || "זמן א'";
    const exitLabel  = dayLabels[moadData.days.length - 1]?.exit || "זמן ב'";
    const EffectiveEntryIcon = WIDGET_ICON_MAP[dayLabels[0]?.entryIcon] || EntryIcon;
    const EffectiveExitIcon  = WIDGET_ICON_MAP[dayLabels[moadData.days.length - 1]?.exitIcon] || ExitIcon;

    // Flatten all custom times sorted by day then time
    const allTimes = [];
    const timesByDay = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!timesByDay[idx]) timesByDay[idx] = [];
        timesByDay[idx].push(ct);
    });
    Object.keys(timesByDay).sort((a, b) => Number(a) - Number(b)).forEach(dayIdx => {
        const group = timesByDay[dayIdx].sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time)));
        group.forEach(ct => allTimes.push({ ...ct, dayColor: days[Number(dayIdx)]?.color || '#166534' }));
    });

    const hasCustomTimes = allTimes.length > 0;
    const hasDrashot = moadData.days.some((_, i) => drashotByDay[i]);
    const showEntry = dayLabels[0]?.entry !== null;
    const showExit  = dayLabels[moadData.days.length - 1]?.exit !== null;

    return (
        <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'row',
            direction: 'rtl', boxSizing: 'border-box',
            backgroundColor: '#fdfbf7',
            overflow: 'hidden',
        }}>
            {/* Panel 1 — Title */}
            {showTitle && (
                <div style={{
                    flex: '0 0 auto',
                    minWidth: 120,
                    maxWidth: '28%',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: '16px 20px',
                    borderLeft: '1px solid #e5ddd3',
                    background: 'radial-gradient(ellipse at 50% 40%, #e8f4ec 0%, #fdfbf7 75%)',
                    gap: 8,
                }}>
                    <span style={{
                        fontSize: '1.75rem', fontWeight: 800,
                        color: '#1e3a5f', textAlign: 'center',
                        lineHeight: 1.2, letterSpacing: '0.01em',
                        wordBreak: 'break-word',
                    }}>
                        {title}
                    </span>
                    <div style={{ width: 28, height: 2, backgroundColor: '#166534', borderRadius: 1, opacity: 0.4 }} />
                </div>
            )}

            {/* Panel 2 — Entry / Exit */}
            {(showEntry || showExit) && (
                <div style={{
                    flex: '0 0 auto',
                    minWidth: 140,
                    maxWidth: '38%',
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '16px 20px',
                    borderLeft: hasCustomTimes ? '1px solid #e5ddd3' : 'none',
                    gap: 0,
                }}>
                    {/* Entry row */}
                    {showEntry && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 0',
                            borderBottom: showExit ? '1px solid #e5ddd3' : 'none',
                        }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: '50%',
                                backgroundColor: '#166534',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <EffectiveEntryIcon style={{ width: 16, height: 16, color: '#fff' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em' }}>
                                    {entryLabel}
                                </div>
                                <div style={{
                                    fontSize: '1.625rem', fontWeight: 800,
                                    color: '#1e3a5f', lineHeight: 1.1,
                                    fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                }}>
                                    {entryTime}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Exit row */}
                    {showExit && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 0',
                        }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: '50%',
                                backgroundColor: '#1e3a5f',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <EffectiveExitIcon style={{ width: 16, height: 16, color: '#fff' }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.06em' }}>
                                    {exitLabel}
                                </div>
                                <div style={{
                                    fontSize: '1.625rem', fontWeight: 800,
                                    color: '#1e3a5f', lineHeight: 1.1,
                                    fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                }}>
                                    {exitTime}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Panel 3 — Drashot */}
            {hasDrashot && (
                <div style={{
                    flex: '0 0 auto',
                    minWidth: 120,
                    maxWidth: '30%',
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    borderLeft: hasCustomTimes ? '1px solid #e5ddd3' : 'none',
                    gap: 8,
                }}>
                    {moadData.days.map((day, dayIdx) => {
                        const drasha = drashotByDay[dayIdx];
                        if (!drasha) return null;
                        const dayColor = days[dayIdx]?.color || '#166534';
                        return (
                            <div key={`drasha-${dayIdx}`}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.65rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.06em',
                                        marginBottom: 2,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <div style={{
                                    fontSize: '0.8rem', fontWeight: 600,
                                    color: '#1e3a5f', fontStyle: 'italic',
                                    whiteSpace: 'pre-wrap', lineHeight: 1.3,
                                }}>
                                    {drasha}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Panel — Leyning */}
            {(showLeyning || showHaftarah) && moadData.days.some(d => d.leyning) && (
                <div style={{
                    flex: '0 0 auto',
                    minWidth: 120,
                    maxWidth: '30%',
                    display: 'flex', flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '12px 16px',
                    borderLeft: hasCustomTimes ? '1px solid #e5ddd3' : 'none',
                    gap: 8,
                }}>
                    {moadData.days.map((day, dayIdx) => {
                        if (!day.leyning) return null;
                        const dayColor = days[dayIdx]?.color || '#166534';
                        return (
                            <div key={`leyning-${dayIdx}`}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.65rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.06em',
                                        marginBottom: 2,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <LeyningDisplay
                                    leyning={day.leyning}
                                    mode={leyningDisplayMode}
                                    showHaftarah={showHaftarah}
                                    showMaftir={leyningDisplayMode === 'full'}
                                    compact={true}
                                    style={{ padding: '8px 0' }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Panel 4 — Custom times */}
            {hasCustomTimes && (
                <div style={{
                    flex: 1,
                    padding: '12px 16px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                    gridAutoRows: 'max-content',
                    gap: '6px 10px',
                    alignContent: 'center',
                    overflow: 'hidden',
                }}>
                    {allTimes.map((ct, idx) => (
                        <div key={idx} style={{
                            display: 'flex', flexDirection: 'column',
                            padding: '6px 10px',
                            borderRadius: 8,
                            backgroundColor: '#fff',
                            borderRight: `3px solid ${ct.dayColor}`,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            overflow: 'hidden',
                        }}>
                            <span style={{
                                fontSize: '0.7rem', fontWeight: 600,
                                color: '#94a3b8', letterSpacing: '0.04em',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                {ct.label}
                            </span>
                            <span style={{
                                fontSize: '1.1rem', fontWeight: 700,
                                color: '#1e3a5f',
                                fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                lineHeight: 1.2,
                            }}>
                                {ct.time}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Ribbon;
